import { Command } from 'commander';
import inquirer from 'inquirer';
import chalk from 'chalk';
import { existsSync, mkdirSync, writeFileSync } from 'fs';
import { execSync } from 'child_process';
import { join } from 'path';
import { hasCredentials, loadConfig, getApiKey, setDefaultWorkspace } from '../lib/config.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { handleError, MutagentError } from '../lib/errors.js';
import {
  FRAMEWORK_DETECTION_MAP,
  detectPackageManager,
  getInstallCommand,
  detectFrameworkFromPackageJson,
  hasRcConfig,
} from '../lib/framework-detection.js';
import type { FrameworkDetection } from '../lib/framework-detection.js';
import type { CLIDirective } from '../lib/directive-types.js';

// Re-export for backwards compatibility
export { detectPackageManager, getInstallCommand, detectFrameworkFromPackageJson, hasRcConfig } from '../lib/framework-detection.js';

// =============================================================================
// Types
// =============================================================================

type InitMode = 'full' | 'cli-only' | 'skip';

interface InitOptions {
  nonInteractive?: boolean;
}

interface MutagentRcConfig {
  defaultWorkspace?: string;
  defaultOrganization?: string;
  endpoint?: string;
  framework?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Write .mutagentrc.json to cwd
 */
function writeRcConfig(config: MutagentRcConfig, cwd: string = process.cwd()): void {
  const rcPath = join(cwd, '.mutagentrc.json');
  writeFileSync(rcPath, JSON.stringify(config, null, 2) + '\n');
}

// =============================================================================
// Init Command
// =============================================================================

export function createInitCommand(): Command {
  const init = new Command('init')
    .description('Initialize MutagenT in your project')
    .option('--non-interactive', 'Skip interactive prompts (defaults to CLI-only mode)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent init                     # Interactive setup wizard
  ${chalk.dim('$')} mutagent init --non-interactive    # CLI-only mode (no prompts)

Modes:
  ${chalk.bold('Full scaffold')}     Install SDK + integration package, create config, setup tracing
  ${chalk.bold('CLI-only')}          Verify auth + create .mutagentrc.json with workspace/endpoint
  ${chalk.bold('Skip')}              Exit without changes
    `)
    .action(async (options: InitOptions) => {
      const isJson = getJsonFlag(init);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const isNonInteractive =
          options.nonInteractive === true ||
          process.env.MUTAGENT_NON_INTERACTIVE === 'true' ||
          process.env.CI === 'true' ||
          !process.stdin.isTTY;

        const cwd = process.cwd();

        // Step 1: Check existing config
        if (hasRcConfig(cwd)) {
          output.warn('.mutagentrc.json already exists in this directory.');
          if (isNonInteractive) {
            output.info('Use --force to overwrite (not yet supported). Exiting.');
            return;
          }
          const { overwrite } = await inquirer.prompt<{ overwrite: boolean }>([{
            type: 'confirm',
            name: 'overwrite',
            message: '.mutagentrc.json already exists. Overwrite?',
            default: false,
          }]);
          if (!overwrite) {
            output.info('Init cancelled.');
            return;
          }
        }

        // Step 2: Check auth
        const authenticated = hasCredentials();
        if (!authenticated) {
          output.warn('Not authenticated.');
          output.info('Run: mutagent auth login --browser');
          if (isNonInteractive) {
            throw new MutagentError(
              'AUTH_REQUIRED',
              'Authentication required before init',
              'Run: mutagent auth login --browser'
            );
          }
          const { doAuth } = await inquirer.prompt<{ doAuth: boolean }>([{
            type: 'confirm',
            name: 'doAuth',
            message: 'Would you like to authenticate now?',
            default: true,
          }]);
          if (doAuth) {
            output.info('Run "mutagent auth login --browser" and then re-run "mutagent init".');
            return;
          }
        }

        // Step 3: Detect framework
        const detectedFramework = detectFrameworkFromPackageJson(cwd);

        // Step 4: Determine mode
        let mode: InitMode;
        let confirmedFramework: FrameworkDetection | null = detectedFramework;

        if (isNonInteractive) {
          mode = 'cli-only';
          if (isJson) {
            output.output({
              mode: 'cli-only',
              nonInteractive: true,
              detectedFramework: detectedFramework?.name ?? null,
            });
          }
        } else {
          // Show detected framework
          if (detectedFramework) {
            output.info(`Detected framework: ${detectedFramework.displayName} (${detectedFramework.npmPackage})`);

            const { confirmFramework } = await inquirer.prompt<{ confirmFramework: boolean }>([{
              type: 'confirm',
              name: 'confirmFramework',
              message: `Use ${detectedFramework.displayName}?`,
              default: true,
            }]);

            if (!confirmFramework) {
              const frameworkChoices = [
                { name: 'Mastra', value: 'mastra' },
                { name: 'LangChain', value: 'langchain' },
                { name: 'LangGraph', value: 'langgraph' },
                { name: 'Vercel AI SDK', value: 'vercel-ai' },
                { name: 'OpenAI SDK', value: 'openai' },
                { name: 'Generic (OpenAI-compatible)', value: 'generic' },
                { name: 'None / Skip framework', value: 'none' },
              ];

              const { selectedFramework } = await inquirer.prompt<{ selectedFramework: string }>([{
                type: 'list',
                name: 'selectedFramework',
                message: 'Select your AI framework:',
                choices: frameworkChoices,
              }]);

              if (selectedFramework === 'none') {
                confirmedFramework = null;
              } else {
                // Find framework in detection map by name
                const entry = Object.values(FRAMEWORK_DETECTION_MAP).find(f => f.name === selectedFramework);
                confirmedFramework = entry ?? null;
              }
            }
          } else {
            output.info('No AI framework detected in package.json.');
          }

          // Ask mode
          const modeChoices = [
            ...(confirmedFramework?.mutagentPackage ? [{
              name: `Full scaffold - Install @mutagent/sdk + ${confirmedFramework.mutagentPackage}, create config, setup tracing`,
              value: 'full' as const,
            }] : []),
            {
              name: 'CLI-only - Verify auth + create .mutagentrc.json config',
              value: 'cli-only' as const,
            },
            {
              name: 'Skip - Exit without changes',
              value: 'skip' as const,
            },
          ];

          const { selectedMode } = await inquirer.prompt<{ selectedMode: InitMode }>([{
            type: 'list',
            name: 'selectedMode',
            message: 'How would you like to initialize MutagenT?',
            choices: modeChoices,
          }]);

          mode = selectedMode;
        }

        // Step 5: Execute based on mode
        if (mode === 'skip') {
          output.info('Init skipped.');
          return;
        }

        // Load config for workspace/endpoint
        const config = loadConfig();
        const endpoint = config.endpoint ?? 'https://api.mutagent.io';
        let workspace = config.defaultWorkspace;

        // Best-effort: validate workspace against API
        let workspaceValidation: { id: string; name: string; validated: boolean; corrected: boolean } | undefined;
        let resolvedOrgId: string | undefined;
        if (authenticated) {
          try {
            const apiKey = getApiKey();
            if (apiKey) {
              const { fetchOrganizations, fetchWorkspaces } = await import('../lib/sdk-client.js');
              let orgId = config.defaultOrganization;
              if (!orgId) {
                const orgs = await fetchOrganizations(apiKey, endpoint);
                orgId = orgs[0]?.id;
              }
              if (orgId) {
                resolvedOrgId = orgId;
                const workspaces = await fetchWorkspaces(apiKey, endpoint, orgId);
                if (workspaces.length > 0) {
                  const found = workspace ? workspaces.find(w => w.id === workspace) : undefined;
                  if (found) {
                    workspaceValidation = { id: found.id, name: found.name, validated: true, corrected: false };
                  } else if (workspaces[0]) {
                    const first = workspaces[0];
                    setDefaultWorkspace(first.id);
                    workspace = first.id;
                    workspaceValidation = { id: first.id, name: first.name, validated: true, corrected: true };
                  }
                }
              }
            }
          } catch {
            // Best-effort — don't block init on network failure
          }
        }

        const rcConfig: MutagentRcConfig = {
          endpoint,
          ...(workspace ? { defaultWorkspace: workspace } : {}),
          ...(resolvedOrgId ? { defaultOrganization: resolvedOrgId } : {}),
          ...(confirmedFramework ? { framework: confirmedFramework.name } : {}),
        };

        if (mode === 'full' && confirmedFramework?.mutagentPackage) {
          // Full scaffold: install packages + create config
          const pm = detectPackageManager(cwd);
          const packages = ['@mutagent/sdk', confirmedFramework.mutagentPackage];
          const installCmd = getInstallCommand(pm, packages);

          output.info(`Installing: ${packages.join(', ')}`);
          output.info(`Running: ${installCmd}`);

          try {
            execSync(installCmd, { cwd, stdio: 'inherit' });
            output.success('Packages installed successfully.');
          } catch {
            output.error('Package installation failed. You can install manually:');
            output.info(`  ${installCmd}`);
          }

          // Write config
          writeRcConfig(rcConfig, cwd);
          output.success('Created .mutagentrc.json');

          // Workspace validation hint
          if (!isJson && workspaceValidation) {
            if (workspaceValidation.corrected) {
              output.warn(`Workspace auto-corrected to: ${workspaceValidation.name} (${workspaceValidation.id})`);
            } else {
              output.info(`Workspace: ${workspaceValidation.name} (${workspaceValidation.id})`);
            }
          }
          if (!isJson) {
            output.info('Verify workspace: mutagent workspaces list --json');
          }

          // Print next steps
          console.log('');
          output.info('Next steps:');
          output.info(`  1. Run: mutagent integrate ${confirmedFramework.name}`);
          output.info('  2. Follow the integration guide to add tracing to your code');
          output.info('  3. Run: mutagent traces list  (to verify traces are arriving)');
        } else {
          // CLI-only mode: just create config
          writeRcConfig(rcConfig, cwd);
          output.success('Created .mutagentrc.json');

          // Workspace validation hint
          if (!isJson && workspaceValidation) {
            if (workspaceValidation.corrected) {
              output.warn(`Workspace auto-corrected to: ${workspaceValidation.name} (${workspaceValidation.id})`);
            } else {
              output.info(`Workspace: ${workspaceValidation.name} (${workspaceValidation.id})`);
            }
          }

          if (!authenticated) {
            output.info('Next: mutagent auth login --browser');
          } else {
            output.info('Ready! Run "mutagent --help" to see available commands.');
            if (!isJson) {
              output.info('Verify workspace: mutagent workspaces list --json');
            }
          }
        }

        // Step 6: Skill + hooks install
        const skillPath = join(cwd, '.claude/skills/mutagent-cli/SKILL.md');
        const skillInstalled = existsSync(skillPath);

        if (!isNonInteractive && !skillInstalled) {
          // Interactive: prompt directly
          const { installSkill } = await inquirer.prompt<{ installSkill: boolean }>([{
            type: 'confirm',
            name: 'installSkill',
            message: 'Install MutagenT skill for Claude Code? (Teaches AI agents how to use the CLI)',
            default: true,
          }]);

          if (installSkill) {
            try {
              const skillDir = join(cwd, '.claude/skills/mutagent-cli');
              if (!existsSync(skillDir)) {
                mkdirSync(skillDir, { recursive: true });
              }
              execSync('node ' + join(cwd, 'node_modules/.bin/mutagent') + ' skills install', {
                cwd,
                stdio: 'ignore',
              });
              output.success('Installed MutagenT CLI skill for Claude Code');
            } catch {
              output.info('Install skill manually: mutagent skills install');
            }
          }
        }

        // Always emit structured summary (default output, not gated on --json)
        const initDirective: CLIDirective = {
          instruction: skillInstalled
            ? 'Verify workspace. Run: mutagent workspaces list --json'
            : [
                'MANDATORY: Ask the user whether they want to install the MutagenT CLI skill and Claude Code hooks.',
                'Use your AskUserQuestion tool to present the following choices:',
                '1. Install skill + hooks (Recommended) — Installs the CLI skill AND configures Claude Code session telemetry hooks',
                '2. Install skill only — Installs the CLI skill (teaches AI agents to use MutagenT CLI)',
                '3. Skip — Do not install',
                '',
                'If the user picks option 1, run BOTH commands:',
                '  mutagent skills install --json',
                '  mutagent hooks install --json',
                '',
                'If the user picks option 2, run only: mutagent skills install --json',
                '',
                'After skill/hooks install, verify workspace: mutagent workspaces list --json',
              ].join('\n'),
          next: ['mutagent workspaces list --json', 'mutagent prompts list --json'],
          ...(!skillInstalled ? {
            askUserQuestions: [{
              question: 'Would you like to install the MutagenT CLI skill and Claude Code hooks?',
              options: [
                { label: 'Install skill + hooks (Recommended)', description: 'Installs the CLI skill AND configures Claude Code session telemetry hooks' },
                { label: 'Install skill only', description: 'Installs the CLI skill (teaches AI agents to use MutagenT CLI)' },
                { label: 'Skip', description: 'Do not install' },
              ],
              multiSelect: false,
            }],
          } : {}),
        };
        const summary = {
          success: true,
          mode,
          configFile: '.mutagentrc.json',
          config: rcConfig,
          framework: confirmedFramework?.name ?? null,
          authenticated,
          workspaceValidation: workspaceValidation ?? null,
          skillInstalled: skillInstalled || existsSync(skillPath), // re-check after interactive install
          _directive: initDirective,
        };

        output.output(summary);
      } catch (error) {
        handleError(error, isJson);
      }
    });

  return init;
}
